import { useCallback, useRef } from 'react';
import { listLookupValues } from '../services/lookupService';
import type { LookupValue } from '../services/lookupService';

interface CacheEntry {
  values: LookupValue[];
  fetchedAt: number;
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes

// Module-level cache shared across all hook instances
const cache = new Map<string, CacheEntry>();

/**
 * Hook providing cached access to lookup values by type.
 * Cache is module-scoped (shared across all InlineEditableField instances)
 * with a 5-minute TTL per lookup type.
 */
export function useLookupCache() {
  const inflightRef = useRef<Map<string, Promise<LookupValue[]>>>(new Map());

  const getLookupValues = useCallback(async (lookupType: string): Promise<LookupValue[]> => {
    // Check cache
    const entry = cache.get(lookupType);
    if (entry && Date.now() - entry.fetchedAt < TTL_MS) {
      return entry.values;
    }

    // Deduplicate in-flight requests
    const inflight = inflightRef.current;
    if (inflight.has(lookupType)) {
      return inflight.get(lookupType)!;
    }

    const promise = listLookupValues(lookupType).then((values) => {
      cache.set(lookupType, { values, fetchedAt: Date.now() });
      inflight.delete(lookupType);
      return values;
    }).catch((err) => {
      inflight.delete(lookupType);
      throw err;
    });

    inflight.set(lookupType, promise);
    return promise;
  }, []);

  const invalidate = useCallback((lookupType?: string) => {
    if (lookupType) {
      cache.delete(lookupType);
    } else {
      cache.clear();
    }
  }, []);

  return { getLookupValues, invalidate };
}
